import api from './api';

export type DiffStatus = 'dev_only' | 'prod_only' | 'different' | 'identical';

export interface RecordRef {
  id: number;
  [k: string]: any;
}

export interface RecordDiff {
  status: DiffStatus;
  natural_key: string;
  dev_record: RecordRef | null;
  prod_record: RecordRef | null;
  diff_fields: string[];
}

export interface TableDiffResponse {
  table: string;
  records: RecordDiff[];
}

export interface TableSummary {
  table: string;
  dev_only: number;
  prod_only: number;
  different: number;
  identical: number;
  error?: string | null;
}

export interface DiffSummaryResponse {
  dev_url: string;
  tables: TableSummary[];
}

export type SyncDirection = 'dev_to_prod' | 'prod_to_dev';

// Where records are read FROM. 'uk' is the peer dev environment, which is
// what this screen has always done. 'il' is the Israeli system, which is
// one-way: records flow into the UK and never back.
export type ConfigSource = 'uk' | 'il';

export interface SyncSource {
  id: ConfigSource;
  label: string;
  tables: string[];
  available: boolean;
  // Why it is unavailable — shown instead of silently hiding the option, so a
  // missing IL_AGENT_API_KEY reads as "not configured" rather than "not a
  // thing this system does".
  detail?: string | null;
  // The Israeli `data` flags that belong in THIS environment, uppercased.
  //
  // The feed used to drop everybody else, which made a user excluded by their
  // flag invisible here rather than merely unsynced. They now all arrive and
  // this is what the users screen preselects, so the usual set is still the
  // default and widening it is a visible act.
  default_data_flags?: string[];
}

export interface SyncRequest {
  table: string;
  ids: number[];
  direction?: SyncDirection;
  source?: ConfigSource;
}

export interface SyncResultItem {
  natural_key: string;
  action: 'created' | 'updated' | 'skipped' | 'error';
  success: boolean;
  error: string | null;
}

export interface SyncResponse {
  table: string;
  results: SyncResultItem[];
}

export const CONFIG_TABLES = [
  // 'users' is filled from Israel only — the peer UK sync has never carried
  // users and still does not. It appears in this list so the IL source can
  // offer it; /config-sync/sources says which tables each source supplies.
  'meeting_types', 'services', 'clinics', 'users', 'referrals', 'summary_types', 'file_types', 'patient_status_configs',
  'patient_types', 'task_rules', 'medications', 'prompts',
  'diagnosis_types', 'wellness_configs', 'wellness_items', 'wellness_tips', 'questionnaire_types',
  'patient_field_configs',
  'email_templates', 'sms_templates', 'patient_notifications',
] as const;

export type ConfigTable = typeof CONFIG_TABLES[number];

export const TABLE_LABELS: Record<ConfigTable, string> = {
  meeting_types: 'Meeting Types',
  services: 'Services',
  clinics: 'Clinics',
  users: 'Therapists',
  referrals: 'Referrals',
  summary_types: 'Summary Types',
  file_types: 'File Types',
  patient_status_configs: 'Patient Statuses',
  patient_types: 'Patient Types',
  task_rules: 'Task Rules',
  medications: 'Medications',
  prompts: 'Prompts',
  diagnosis_types: 'Diagnosis Types',
  wellness_configs: 'Wellness Configs',
  wellness_items: 'Wellness Items',
  wellness_tips: 'Wellness Tips',
  questionnaire_types: 'Questionnaire Types',
  patient_field_configs: 'Patient Field Configs',
  email_templates: 'Email Templates',
  sms_templates: 'SMS Templates',
  patient_notifications: 'Patient Notifications',
};

export interface ConfigSyncStatus {
  available: boolean;
  dev_url: string | null;
}

export interface InitializeMappingTableReport {
  table: string;
  mapped: number;
  dev_only: number;
  prod_only: number;
  ambiguous: number;
}

export interface IlReconcileReport {
  feed_rows: number;
  mapped_active: number;
  to_deactivate: { id: number; email: string; il_user_id: number | null }[];
  deactivated: number;
  dry_run: boolean;
  /** Set when the pass refused to touch anything, and why. */
  refused: string | null;
}

export interface InitializeMappingResponse {
  tables: InitializeMappingTableReport[];
}

export const configSyncService = {
  async getStatus(): Promise<ConfigSyncStatus> {
    const response = await api.get('/config-sync/status');
    return response.data;
  },

  async getSources(): Promise<{ sources: SyncSource[]; default: ConfigSource | null }> {
    const response = await api.get('/config-sync/sources');
    return response.data;
  },

  async getDiffSummary(source: ConfigSource = 'uk'): Promise<DiffSummaryResponse> {
    const response = await api.get('/config-sync/diff', { params: { source } });
    return response.data;
  },

  async getTableDiff(table: ConfigTable, source: ConfigSource = 'uk'): Promise<TableDiffResponse> {
    const response = await api.get('/config-sync/diff', { params: { table, source } });
    return response.data;
  },

  async syncRecords(request: SyncRequest): Promise<SyncResponse> {
    const response = await api.post('/config-sync/sync', request);
    return response.data;
  },

  async initializeMapping(table?: string): Promise<InitializeMappingResponse> {
    const response = await api.post('/config-sync/initialize-mapping', table ? { table } : {});
    return response.data;
  },

  /** Turn off UK accounts whose Israeli user has left the feed.
   *
   * The half of deactivation the diff cannot show: somebody still in the feed
   * with active=false comes through an ordinary sync, but a person who has
   * VANISHED from it appears only as UK-Only, which is not actionable for
   * Israel - so their account would stay live for ever.
   *
   * Always dry-run first. The pass refuses outright on an empty feed or an
   * implausible number of departures, and that refusal is the thing worth
   * reading before anybody runs it in earnest.
   */
  async reconcileIlUsers(dryRun: boolean): Promise<IlReconcileReport> {
    const response = await api.post(
      `/config-sync/il-users/reconcile?dry_run=${dryRun ? 'true' : 'false'}`,
    );
    return response.data;
  },
};
